/**
 * ErrorBoundary - Catches errors in child components and displays fallback UI
 */

import { Component, ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import { i18n } from '@/i18n'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ChatV2 ErrorBoundary caught an error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-dark-accent flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-gray-500 dark:text-dark-text-muted" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">
            {i18n.t('chatv2:errors.something_went_wrong', { defaultValue: 'Something went wrong' })}
          </h3>
          <p className="text-sm text-gray-500 dark:text-dark-text-muted mb-4 max-w-md">
            {i18n.t('chatv2:errors.boundary_no_answer', { defaultValue: "I wasn't able to find an answer for you. Please try again or rephrase your question." })}
          </p>
          <button
            onClick={this.handleRetry}
            className={clsx(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg',
              'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900',
              'hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors'
            )}
          >
            <RefreshCw className="h-4 w-4" />
            {i18n.t('chatv2:errors.try_again', { defaultValue: 'Try again' })}
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
